import { LegalOrganizationManager } from '@shared/interfaces/profile/legal-organization-manager';
import { DefaultWarehouse } from '@shared/interfaces/users/default-warehouse';

// A manager contact is considered empty when it carries no usable personal contact
// (no name, phone number, phones or emails). Such a manager triggers the warehouse fallback.
export function isManagerContactEmpty(manager: LegalOrganizationManager | null): boolean {
  if (!manager) {
    return true;
  }

  return !manager.name?.trim() && !manager.phoneNumber?.trim() && !hasContactItems(manager.phones) && !hasContactItems(manager.emails);
}

// Builds a synthetic manager that carries only the default warehouse (branch) contacts.
export function createWarehouseFallbackManager(warehouse: DefaultWarehouse | null): LegalOrganizationManager | null {
  if (!warehouse) {
    return null;
  }

  return {
    id: warehouse.id,
    name: null,
    phoneNumber: null,
    phones: null,
    emails: null,
    warehouseAddress: warehouse.address,
    warehousePhone: warehouse.phone,
  };
}

// Returns the manager itself when it has real contacts, otherwise falls back to the
// default warehouse contact. The first non-empty source is taken as a whole.
export function applyWarehouseFallback(
  manager: LegalOrganizationManager | null,
  warehouse: DefaultWarehouse | null,
): LegalOrganizationManager | null {
  if (!isManagerContactEmpty(manager)) {
    return manager;
  }

  return createWarehouseFallbackManager(warehouse) ?? manager;
}

export function hasContactItems<T>(items?: T[] | null): items is T[] {
  return Array.isArray(items) && items.length > 0;
}

// A resolved manager contact is displayable when it carries any visible field:
// personal contacts (name/phone/phones/emails) or warehouse contacts (address/phone).
// When nothing is displayable the caller falls through to the hardcoded office echelon.
export function hasDisplayableManagerContact(manager: LegalOrganizationManager | null): boolean {
  if (!manager) {
    return false;
  }

  return !isManagerContactEmpty(manager) || !!manager.warehouseAddress?.trim() || !!manager.warehousePhone?.trim();
}
